import { useState, useMemo } from 'react';
import { Layout } from '@/components/layout/Layout';
import { DeviceCard } from '@/components/common/DeviceCard';
import {
  MetricCardsRowSkeleton,
  DeviceGridSkeleton,
  DistributionBarSkeleton,
} from '@/components/common/Skeletons';
import { Smartphone, AlertTriangle, Wifi, Activity } from 'lucide-react';
import { PlatformIcon } from '@/components/common/PlatformIcon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MetricCard } from '@/components/common/MetricCard';
import { useRealtimeDevices } from '@/hooks/useRealtimeDevices';
import { useDevices } from '@/hooks/useDevices';
import { Skeleton } from '@/components/ui/skeleton';
import { GradeNumeros, ItemLista, ListaMobile, Selo } from '@/components/common/ListaMobile';
import { nomePlataforma, tempoCurto } from '@/utils/aparelho';
import { DetalheAparelhoModal, IconeAparelho } from './AparelhoMobile';

export function Devices() {
  useRealtimeDevices();
  const [platformFilter, setPlatformFilter] = useState<'all' | 'ios' | 'android'>('all');
  const [osVersionFilter, setOsVersionFilter] = useState<'all' | string>('all');

  const { data, isLoading, error } = useDevices({
    platform: platformFilter !== 'all' ? platformFilter : undefined,
    osVersion: osVersionFilter !== 'all' ? osVersionFilter : undefined,
  });

  const devices = useMemo(() => data?.devices || [], [data]);
  const onlineCount = devices.filter((d) => d.status === 'online').length;
  const total = devices.length;
  const withIssues = devices.filter((d) => (d.errors || 0) > 0 || (d.crashes || 0) > 0).length;
  const totalSessions = devices.reduce((sum, d) => sum + (d.sessions || 0), 0);
  const iosCount = devices.filter((d) => d.platform === 'ios').length;
  const androidCount = devices.filter((d) => d.platform === 'android').length;
  const otherCount = Math.max(0, total - iosCount - androidCount);

  // Aparelho aberto no detalhe do celular; lido da lista para acompanhar o tempo real.
  const [detalheId, setDetalheId] = useState<string | null>(null);
  const detalhe = devices.find((d) => d.id === detalheId) ?? null;

  const uniqueOsVersions = useMemo(
    () => Array.from(new Set(devices.map((d) => d.osVersion))),
    [devices]
  );

  if (error) {
    return (
      <Layout breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Dispositivos' }]}>
        <div className="p-3 sm:p-4 lg:p-6">
          <div className="flex h-96 items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-center">
              <AlertTriangle className="size-12 text-error" />
              <h2 className="text-xl font-semibold text-text-title">
                Erro ao carregar dispositivos
              </h2>
              <p className="max-w-sm text-sm text-text-secondary">
                Não foi possível carregar a lista de dispositivos. Tente novamente.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Dispositivos' }]}>
      <div className="p-3 sm:p-4 lg:p-6">
        {/* Cabeçalho */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="max-md:hidden text-2xl font-semibold tracking-tight text-text-title sm:text-3xl">
              Dispositivos Conectados
            </h1>
            <p className="text-sm text-text-secondary">
              Monitore o parque de aparelhos conectados ao app.
            </p>
          </div>

          {/* Filtros */}
          <div className="flex flex-wrap items-center gap-3 max-md:grid max-md:w-full max-md:grid-cols-1">
            <Select
              value={platformFilter}
              onValueChange={(v) => setPlatformFilter(v as typeof platformFilter)}
            >
              <SelectTrigger className="min-w-[150px] gap-2 max-md:w-full max-md:min-w-0">
                <span className="text-text-secondary">Plataforma:</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="ios">
                  <span className="inline-flex items-center gap-2">
                    <PlatformIcon platform="ios" size={13} />
                    iOS
                  </span>
                </SelectItem>
                <SelectItem value="android">
                  <span className="inline-flex items-center gap-2">
                    <PlatformIcon platform="android" size={13} />
                    Android
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>

            <Select value={osVersionFilter} onValueChange={(v) => setOsVersionFilter(v)}>
              <SelectTrigger className="min-w-[170px] gap-2 max-md:w-full max-md:min-w-0">
                <span className="text-text-secondary">Versão:</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {uniqueOsVersions.map((version) => (
                  <SelectItem key={version} value={version}>
                    {version}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary cards */}
        {isLoading ? (
          <div className="mt-4 lg:mt-6">
            <MetricCardsRowSkeleton count={4} />
          </div>
        ) : (
          // No celular os números ficam lado a lado num bloco só; sessões
          // totais vão para o detalhe de cada aparelho.
          <GradeNumeros
            className="mt-4 lg:hidden"
            itens={[
              { rotulo: 'online agora', valor: onlineCount, cor: 'text-online' },
              { rotulo: 'aparelhos', valor: total },
              {
                rotulo: 'com problemas',
                valor: withIssues,
                cor: withIssues > 0 ? 'text-warning' : undefined,
              },
            ]}
          />
        )}
        {!isLoading && (
          <div className="mt-4 hidden grid-cols-2 gap-3 sm:gap-4 lg:mt-6 lg:grid lg:gap-6 xl:grid-cols-4">
            <MetricCard
              title="Online agora"
              value={onlineCount}
              subtitle={`de ${total} no total`}
              icon={Wifi}
              accent="tertiary"
              progress={{ value: onlineCount, max: total || 1 }}
            />
            <MetricCard
              title="Total de dispositivos"
              value={total}
              subtitle="conectados"
              icon={Smartphone}
              accent="primary"
            />
            <MetricCard
              title="Com problemas"
              value={withIssues}
              subtitle={`de ${total} no total`}
              icon={AlertTriangle}
              accent="warning"
              valueColor={withIssues > 0 ? 'text-warning' : undefined}
              progress={{ value: withIssues, max: total || 1 }}
            />
            <MetricCard
              title="Sessões totais"
              value={totalSessions.toLocaleString('pt-BR')}
              subtitle="acumuladas"
              icon={Activity}
              accent="primary"
            />
          </div>
        )}

        {/* Distribuição por plataforma (dado real) */}
        {isLoading && (
          <div className="mt-3 sm:mt-4 lg:mt-6">
            <DistributionBarSkeleton />
          </div>
        )}
        {!isLoading && total > 0 && (
          <div className="mt-3 rounded-xl border border-border bg-card p-4 shadow-card sm:mt-4 lg:mt-6 lg:p-5">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-text-title">Distribuição por plataforma</h3>
              <span className="text-xs tabular-nums text-text-secondary">{total} aparelhos</span>
            </div>
            <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
              {iosCount > 0 && (
                <div
                  className="bg-primary"
                  style={{ width: `${(iosCount / total) * 100}%` }}
                  title={`iOS: ${iosCount}`}
                />
              )}
              {androidCount > 0 && (
                <div
                  className="bg-tertiary"
                  style={{ width: `${(androidCount / total) * 100}%` }}
                  title={`Android: ${androidCount}`}
                />
              )}
              {otherCount > 0 && (
                <div
                  className="bg-border-strong"
                  style={{ width: `${(otherCount / total) * 100}%` }}
                  title={`Outros: ${otherCount}`}
                />
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
              <span className="inline-flex items-center gap-1.5 text-text-secondary">
                <span className="size-2 rounded-full bg-primary" />
                <PlatformIcon platform="ios" size={12} colored />
                iOS
                <span className="font-semibold tabular-nums text-text-title">{iosCount}</span>
              </span>
              <span className="inline-flex items-center gap-1.5 text-text-secondary">
                <span className="size-2 rounded-full bg-tertiary" />
                <PlatformIcon platform="android" size={12} colored />
                Android
                <span className="font-semibold tabular-nums text-text-title">{androidCount}</span>
              </span>
              {otherCount > 0 && (
                <span className="inline-flex items-center gap-1.5 text-text-secondary">
                  <span className="size-2 rounded-full bg-border-strong" />
                  Outros
                  <span className="font-semibold tabular-nums text-text-title">{otherCount}</span>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Lista de aparelhos no celular: tocar abre o detalhe com Logs e Erros */}
        {isLoading ? (
          <ListaMobile className="mt-4 lg:hidden">
            {Array.from({ length: 6 }).map((_, i) => (
              <ItemLista
                key={i}
                inicio={<Skeleton className="size-10 rounded-xl" />}
                titulo={<Skeleton className="h-4 w-32" />}
                apoio={<Skeleton className="h-3 w-44" />}
              />
            ))}
          </ListaMobile>
        ) : (
          devices.length > 0 && (
            <ListaMobile className="mt-4 lg:hidden">
              {devices.map((device) => {
                const problemas = device.errors || 0;
                return (
                  <ItemLista
                    key={device.id}
                    inicio={<IconeAparelho online={device.status === 'online'} />}
                    titulo={device.model}
                    apoio={[
                      device.userName,
                      [nomePlataforma(device.platform), device.osVersion].filter(Boolean).join(' '),
                      device.appVersion && `app ${device.appVersion}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    fim={
                      <>
                        <span>{tempoCurto(device.lastActivity)}</span>
                        {problemas > 0 ? (
                          <Selo tom="aviso">
                            {problemas} {problemas === 1 ? 'erro' : 'erros'}
                          </Selo>
                        ) : (
                          (device.crashes || 0) > 0 && (
                            <Selo tom="erro">
                              {device.crashes} {device.crashes === 1 ? 'crash' : 'crashes'}
                            </Selo>
                          )
                        )}
                      </>
                    }
                    onClick={() => setDetalheId(device.id)}
                  />
                );
              })}
            </ListaMobile>
          )
        )}
        <DetalheAparelhoModal device={detalhe} onClose={() => setDetalheId(null)} />

        {/* Grid de dispositivos */}
        {isLoading ? (
          <div className="mt-4 hidden lg:mt-6 lg:block">
            <DeviceGridSkeleton count={8} />
          </div>
        ) : (
          <div
            className={`mt-4 grid-cols-1 gap-4 sm:gap-5 lg:mt-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 ${
              devices.length > 0 ? 'hidden lg:grid' : 'grid'
            }`}
          >
            {devices.length > 0 ? (
              devices.map((device) => (
                <DeviceCard
                  key={device.id}
                  id={device.id}
                  name={device.model}
                  user={device.userName}
                  status={device.status}
                  platform={device.platform === 'ios' ? 'iOS' : 'Android'}
                  appVersion={device.appVersion}
                  osVersion={device.osVersion}
                  lastActivity={device.lastActivity}
                  sessions={device.sessions}
                  errors={device.errors}
                  crashes={device.crashes}
                />
              ))
            ) : (
              <div className="col-span-full flex flex-col items-center justify-center gap-3 py-16 text-center">
                <Smartphone className="size-12 text-text-hint" />
                <div className="flex flex-col gap-1">
                  <p className="text-base font-medium text-text-title">
                    Ainda não houve conexões de aparelhos
                  </p>
                  <p className="max-w-sm text-sm text-text-secondary">
                    Os dispositivos aparecerão aqui assim que fizerem a primeira conexão com o app.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
